// Deadline calculator — computes a due date from a trigger date + rule.
// This is a simplified model. Real court-rule engines consider local rules,
// federal/state holidays, electronic vs. mail service, etc. This covers the
// 80% case: offset in days with optional business-day skipping.

const US_FEDERAL_HOLIDAYS_2026 = new Set([
  // (month-day). Keep simple; fine-tune per year.
  '01-01','01-19','02-16','05-25','06-19','07-03','09-07','10-12','11-11','11-26','12-25',
]);

const isWeekend = (d) => {
  const day = d.getDay();
  return day === 0 || day === 6;
};

const isHoliday = (d) => {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return US_FEDERAL_HOLIDAYS_2026.has(`${mm}-${dd}`);
};

const shiftToNextBusinessDay = (d) => {
  const out = new Date(d);
  while (isWeekend(out) || isHoliday(out)) {
    out.setDate(out.getDate() + 1);
  }
  return out;
};

export const computeDeadline = ({
  triggerDate,
  offsetDays,
  businessDays = false,
  bumpWeekendsToMonday = true,
} = {}) => {
  if (!triggerDate || offsetDays == null) return null;
  const d = new Date(triggerDate);
  if (Number.isNaN(d.getTime())) return null;

  if (businessDays) {
    let remaining = Math.abs(offsetDays);
    const direction = offsetDays >= 0 ? 1 : -1;
    while (remaining > 0) {
      d.setDate(d.getDate() + direction);
      if (!isWeekend(d) && !isHoliday(d)) remaining -= 1;
    }
  } else {
    d.setDate(d.getDate() + offsetDays);
    if (bumpWeekendsToMonday && (isWeekend(d) || isHoliday(d))) {
      return shiftToNextBusinessDay(d);
    }
  }
  return d;
};

// Compute a whole chain: given a trigger date + list of rules, return deadlines.
export const computeDeadlineChain = (triggerDate, rules = []) =>
  rules.map((r) => ({
    rule: r,
    dueDate: computeDeadline({
      triggerDate,
      offsetDays: r.offsetDays,
      businessDays: r.businessDays,
    }),
  }));

// Business days between two dates (inclusive of end, exclusive of start).
export const businessDaysBetween = (a, b) => {
  const start = new Date(a); const end = new Date(b);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
  if (end < start) return -businessDaysBetween(b, a);
  let n = 0;
  const cur = new Date(start);
  while (cur < end) {
    cur.setDate(cur.getDate() + 1);
    if (!isWeekend(cur) && !isHoliday(cur)) n += 1;
  }
  return n;
};
